import _ from 'lodash'
import i18n from '@/lang'
import {
  GameDifficultyCategory,
  GameDifficultyCategoryId,
  GameDifficultyTag,
} from '@/types/dmaker/game-config.type'
import { CookedCategory } from '@/types/dmaker/category.type'
import { getBaseResourceType } from '@/store/creative/common/resource'
import random from '@com/utils/random'
import { Category, CategoryRef } from '@/types/dmaker/info.type'

export const gameConfigCategoryResourceType = 'gameConfig'
export const i18nCategoryResourceType = 'i18n'
export const globalConfigCategoryResourceType = 'globalConfig'
export const presetConfigParentCategoryType = 'presetConfigParent'
export const presetConfigCategoryResourceType = 'presetConfig'
export const presetCategoryResourceType = 'preset'
export const presetOptionCategoryResourceType = 'presetOption'
export const cameraScreenConfigType = '3DScreenConfig'
export const cameraSceneConfigType = '3DSceneConfig'
export const threeDimensionalMaterialResourceType = '3DMaterial'
export const threeDimensionalModelTextureResourceType = 'texture'

export const categoryPrefix = {
  bgColor: 'bgColor',
}

/**
 * 生成通用玩法分类一个 15 位的 id，后端根据 id 的大小去判断（在有些地方是生成的是时间戳）。大于 10 的 13 次方的都认为是前端的 id
 */
function createCommonGameConfigFeCategoryId(val: number) {
  return Math.pow(10, 15) + val
}

/**
 * 生成一个 13 位的 id，后端根据 id 的大小去判断（在有些地方是生成的是时间戳）。大于 10 的 13 次方的都认为是前端的 id
 * 不要使用 10 的 15 次方，因为可能与一些固定 id 冲突
 * @returns {number}
 */
export function createFeGameConfigCategoryId(): number {
  return Number(random(13))
}

/**
 * 5 位就是前段生成的模型 id
 */
export function createFeModelId(): string {
  return String(random(5))
}

export function isFeModelId(id: string): boolean {
  return !isNaN(id) && id.length === 5
}

export const gameDifficultyCategoryConfig: {
  [key: string]: {
    id: number
    prefix: string
  }
} = {
  easy: {
    id: createCommonGameConfigFeCategoryId(10),
    prefix: 'easyGameDifficulty',
  },
  medium: {
    id: createCommonGameConfigFeCategoryId(11),
    prefix: 'mediumGameDifficulty',
  },
  hard: {
    id: createCommonGameConfigFeCategoryId(12),
    prefix: 'hardGameDifficulty',
  },
}

export const categoryConfig: {
  [key: string]: {
    id: number
    prefix: string
  }
} = {
  // 玩法
  gamePlay: {
    id: 3,
    prefix: '',
  },
  // 元素
  element: {
    id: 1,
    prefix: '',
  },
  // 拓展内容
  expandedContent: {
    id: 4,
    prefix: '',
  },
  // 引导环节
  startScene: {
    id: 5,
    prefix: '',
  },
  // 试玩环节
  gameScene: {
    id: 6,
    prefix: '',
  },
  // 结束环节
  endingScene: {
    id: 7,
    prefix: '',
  },
  // 通用元素
  commonAssets: {
    id: 8,
    prefix: '',
  },
  // 场景列表分类
  scene: {
    id: 9,
    prefix: '',
  },
  // 资源预设
  preset: {
    id: 10,
    prefix: '',
  },
  threeDimensionalResource: {
    id: 11,
    prefix: '',
  },
  // 元素收藏
  elementStars: {
    id: createCommonGameConfigFeCategoryId(1),
    prefix: 'elementStarsCategory',
  },
  // 玩法收藏
  gameConfigStars: {
    id: createCommonGameConfigFeCategoryId(2),
    prefix: 'gameConfigStarsCategory',
  },
  // 多组合元素分类
  multiCustomCategory: {
    id: createCommonGameConfigFeCategoryId(3),
    prefix: 'multiCustomCategory',
  },
  // 试玩次数
  playAgain: {
    id: createCommonGameConfigFeCategoryId(4),
    prefix: 'playAgain',
  },
  // 无操作时限
  autoEndTime: {
    id: createCommonGameConfigFeCategoryId(5),
    prefix: 'autoEndTime',
  },
  // 全局可点
  globalClick: {
    id: createCommonGameConfigFeCategoryId(6),
    prefix: 'global_click',
  },
  // 诱导点击
  ydClick: {
    id: createCommonGameConfigFeCategoryId(7),
    prefix: 'yd_click',
  },
  // 自动点击跳转
  autoClick: {
    id: createCommonGameConfigFeCategoryId(8),
    prefix: 'auto_click',
  },
  // 颜色配置
  colorConfig: {
    id: createCommonGameConfigFeCategoryId(9),
    prefix: 'colorConfig',
  },
}

export function isGlobalCategory(id: number): boolean {
  return [
    categoryConfig.globalClick!.id,
    categoryConfig.ydClick!.id,
    categoryConfig.autoClick!.id,
    categoryConfig.autoEndTime!.id,
    categoryConfig.elementStars!.id,
    categoryConfig.gameConfigStars!.id,
    categoryConfig.multiCustomCategory!.id,
    categoryConfig.colorConfig!.id,
    gameDifficultyCategoryConfig.easy!.id,
    gameDifficultyCategoryConfig.medium!.id,
    gameDifficultyCategoryConfig.hard!.id,
  ].some((j) => j === id)
}

// 判断是否是前端生成的玩法分类
export const isFeGameConfigCategory = (
  category: GameDifficultyCategory,
): boolean => {
  return (
    category.id >= Math.pow(10, 12) &&
    category.resource_type === gameConfigCategoryResourceType
  )
}

// 判断是否是前端生成的预设分类
export const isFePresetCategory = (category: CookedCategory): boolean => {
  return category.resource_type === presetConfigCategoryResourceType
}

export const isCameraCategory = (category: CookedCategory): boolean => {
  return (
    category.resource_type === cameraScreenConfigType ||
    category.resource_type === cameraSceneConfigType
  )
}

export const is3DResourceCategory = (category: CookedCategory): boolean => {
  return category.id === categoryConfig.threeDimensionalResource!.id
}

export const is3DMaterialCategory = (category: CookedCategory): boolean => {
  return category.sub_resource_type === threeDimensionalMaterialResourceType
}

export const is3DTextureCategory = (category: CookedCategory): boolean => {
  return category.sub_resource_type === threeDimensionalModelTextureResourceType
}

export const isPresetCategory = (
  category: CookedCategory | Category,
): boolean => {
  return category.resource_type === presetCategoryResourceType
}

export const isPresetOptionCategory = (category: CookedCategory): boolean => {
  return category.resource_type === presetOptionCategoryResourceType
}

export const isPresetConfigParentCategory = (
  category: CookedCategory,
): boolean => {
  return category.resource_type === presetConfigParentCategoryType
}

export const getGameDifficultyCategoryIdByTag = (tag: string): number => {
  return gameDifficultyCategoryConfig[tag]!.id
}

export const getGameDifficultyCategoryPrefixByTag = (tag: string): string => {
  return gameDifficultyCategoryConfig[tag]!.prefix
}

export const gameDifficultyCategoryList: GameDifficultyCategoryId[] =
  Object.values(gameDifficultyCategoryConfig).map((item) => item.id)

export function cookGlobalClickCategory({ pid }: { pid: number }) {
  return {
    pid,
    id: categoryConfig.globalClick!.id,
    prefix: categoryConfig.globalClick!.prefix,
    name: i18n.t('playTurbo.gamePlay.global_click.name') as string,
    resource_type: gameConfigCategoryResourceType,
    priority: -1,
  }
}

export function cookAutoClickCategory({ pid }: { pid: number }) {
  return {
    pid,
    id: categoryConfig.autoClick!.id,
    prefix: categoryConfig.autoClick!.prefix,
    name: i18n.t('playTurbo.gamePlay.auto_click.name') as string,
    resource_type: gameConfigCategoryResourceType,
    priority: -1,
  }
}

export function cookYdClickCategory({ pid }: { pid: number }) {
  return {
    pid,
    id: categoryConfig.ydClick!.id,
    prefix: categoryConfig.ydClick!.prefix,
    name: i18n.t('playTurbo.gamePlay.yd_click.name') as string,
    resource_type: gameConfigCategoryResourceType,
    priority: -1,
  }
}

export function cookAutoEndTimeCategory({ pid }: { pid: number }) {
  return {
    pid,
    id: categoryConfig.autoEndTime!.id,
    prefix: categoryConfig.autoEndTime!.prefix,
    name: i18n.t('playTurbo.gamePlay.autoEndTime.name') as string,
    resource_type: gameConfigCategoryResourceType,
    priority: -1,
  }
}

export function cookPlayAgainCategory({ pid }: { pid: number }) {
  return {
    pid,
    id: categoryConfig.playAgain!.id,
    prefix: categoryConfig.playAgain!.prefix,
    name: i18n.t('playTurbo.gamePlay.playAgain.name') as string,
    resource_type: gameConfigCategoryResourceType,
    priority: -1,
  }
}

export function cookColorConfigCategory({ pid }: { pid: number }) {
  return {
    pid,
    id: categoryConfig.colorConfig!.id,
    prefix: categoryConfig.colorConfig!.prefix,
    name: i18n.t('playTurbo.gamePlay.colorConfig.name') as string,
    resource_type: gameConfigCategoryResourceType,
    priority: -1,
  }
}

export function cookGameDifficultyCategory({
  pid,
  tag,
}: {
  pid: string | number
  tag: GameDifficultyTag
}): GameDifficultyCategory {
  return {
    pid,
    id: getGameDifficultyCategoryIdByTag(tag),
    prefix: getGameDifficultyCategoryPrefixByTag(tag),
    tag,
    name: i18n.t(`playTurbo.gamePlay.difficulty.${tag}.name`) as string,
    resource_type: gameConfigCategoryResourceType,
  }
}

/**
 * 查找分类的后代分类中匹配到的第一个分类
 */
export function findCatgeory(
  category: CookedCategory,
  callback: (item: CookedCategory) => boolean,
): CookedCategory | undefined {
  if (callback(category)) {
    return category
  } else if (category.sub_category && category.sub_category.length > 0) {
    for (let index = 0; index < category.sub_category.length; index++) {
      const current = findCatgeory(category.sub_category[index]!, callback)
      if (current) return current
    }
  }
}

/**
 * 返回满足条件的所有分类的数组
 */
export function findMatchingCategories(
  category: CookedCategory,
  callback: (item: CookedCategory) => boolean,
): CookedCategory[] {
  let matchingCategories: CookedCategory[] = []

  if (callback(category)) {
    matchingCategories.push(category)
  }

  if (category.sub_category && category.sub_category.length > 0) {
    for (let index = 0; index < category.sub_category.length; index++) {
      const subCategory = category.sub_category[index]!
      const matchingSubCategories = findMatchingCategories(
        subCategory,
        callback,
      )
      matchingCategories = matchingCategories.concat(matchingSubCategories)
    }
  }

  return matchingCategories
}

export function traverseCategory(
  category: CookedCategory,
  callback: (category: CookedCategory) => void,
): void {
  callback(category)
  if (category.sub_category && category.sub_category.length > 0) {
    _.each(category.sub_category, (item) => traverseCategory(item, callback))
  }
}

/**
 * 获取分类的镜像引用节点 id 列表
 */
export function getCategoryRef(
  category: CookedCategory,
  ref: CategoryRef,
): number[] | void {
  const baseResourceType = getBaseResourceType(category.resource_type) as string
  const prefix = _.get(category, 'properties.refId') || category.prefix
  const currentRef: number[] = _.get(ref, [baseResourceType, prefix])
  if (currentRef) {
    return currentRef
      .filter((item) => Number(item) !== Number(category.id))
      .map((item) => Number(item))
  }
}
